/**
 * main.tsx
 * Bootstrap order (Mable_Scaffold pattern):
 *  1. Initialize msalInstance
 *  2. Handle redirect promise (processes auth code from AAD redirect)
 *  3. Guard against hidden iframe (MSAL silent renewal — must not mount app)
 *  4. Set active MSAL account
 *  5. Wire setTokenAcquirer so apiClient can acquire tokens without importing MSAL
 *  6. Mount React app (ProtectedRoute handles any remaining unauthenticated state)
 */
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { msalInstance, apiTokenRequest } from '@/features/auth/authConfig'
import { setTokenAcquirer } from '@/lib/apiClient'
import '@/styles/index.css'
import { App } from '@/app/App'

async function bootstrap() {
  // ── Dev bypass: VITE_DISABLE_AUTH=true skips all MSAL initialisation ────────
  // API calls use X-Api-Key header (set VITE_API_KEY in .env.local).
  if (import.meta.env.VITE_DISABLE_AUTH === 'true') {
    const rootEl = document.getElementById('root')
    if (!rootEl) throw new Error('Root element #root not found in index.html')
    createRoot(rootEl).render(
      <StrictMode>
        <App />
      </StrictMode>,
    )
    return
  }

  // 1. Initialise MSAL before any other call
  await msalInstance.initialize()

  // 2. Process the redirect response (must be called on every page load)
  await msalInstance.handleRedirectPromise().catch(console.error)

  // 3. Do not mount app inside hidden iframes (MSAL silent token renewal)
  if (window !== window.parent || window.opener) return

  // 4. Set active account from the first available account
  const accounts = msalInstance.getAllAccounts()
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0])
  }

  // 5. Inject MSAL token acquisition into the fetch client (decoupled from MSAL)
  setTokenAcquirer(async () => {
    const account = msalInstance.getActiveAccount()
    if (!account) return ''
    try {
      const result = await msalInstance.acquireTokenSilent({
        ...apiTokenRequest,
        account,
      })
      return result.accessToken
    } catch {
      // Interaction will be triggered by ProtectedRoute if silent fails
      return ''
    }
  })

  // 6. Mount the React app — ProtectedRoute handles unauthenticated state
  const rootElement = document.getElementById('root')
  if (!rootElement) throw new Error('Root element #root not found in index.html')

  createRoot(rootElement).render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
}

bootstrap().catch(console.error)

